<template>
  <div class="border-container-unique38 mb-4">
    <div class="admin-content-layout">
      <div
        class="admin-main-content"
        style="
          padding-top: 0px;
          max-width: 100%;
          padding-right: 0px;
          overflow-x: hidden;
        "
      >
        <!-- Header Section -->
        <div class="d-flex justify-space-between align-items-center mb-4">
          <div class="admin-header">
            <GraduationCapIcon width="24" height="24" />
            <h1>Trainer Academy</h1>
          </div>
        </div>

        <!-- Main Content Section -->
        <div class="trainer-academy-container mb-4">
          <h2 class="trainer-academy-title">
            Manage trainers, track progress, and update course content
          </h2>

          <!-- Metrics Cards -->
          <div class="metrics-row mb-4">
            <div class="metric-card">
              <div class="metric-label">Total Trainers</div>
              <div class="metric-value">{{ totalTrainers }}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Certificates Issued</div>
              <div class="metric-value">{{ certificatesIssued }}</div>
            </div>
          </div>

          <!-- Trainers Table -->
          <div class="trainers-table-container">
            <TableData
              :headers="tableHeaders"
              :items="trainers"
              :loading="loading"
              :total-pages="totalPages"
              :current-page="currentPage"
              :show-data-info="true"
              :total-rows="trainersData?.total_rows || 0"
              :current-limit="itemsPerPage"
              :use-api-sort="true"
              variant="rounded"
              @update:current-page="updatePage"
              @sort="handleSort"
            >
              <template #cell-date_added="{ value }">
                {{ value ? new Date(value).toLocaleDateString() : "-" }}
              </template>

              <template #cell-progress="{ value }">
                <div class="progress-cell">
                  <div class="progress-bar-container">
                    <div
                      class="progress-bar-fill"
                      :style="{ width: value + '%' }"
                    ></div>
                  </div>
                  <span class="progress-text">{{ value }}%</span>
                </div>
              </template>

              <template #cell-status="{ value }">
                <span
                  class="status-badge"
                  :class="
                    value === 'Certified'
                      ? 'status-certified'
                      : 'status-in-training'
                  "
                >
                  {{ value }}
                </span>
              </template>

              <template #cell-action="{ item }">
                <button
                  class="view-details-button"
                  @click="handleViewDetails(item)"
                >
                  <EyeIcon width="24" height="24" />
                  View Details
                </button>
              </template>
            </TableData>
          </div>
        </div>
      </div>
    </div>

    <!-- Trainer Detail Modal -->
    <TrainerDetailModal
      v-model="showModal"
      :trainer="selectedTrainer"
      @close="handleCloseModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import TableData from "@/components/common/TableData.vue";
import GraduationCapIcon from "@/components/icons/GraduationCapIcon.vue";
import EyeIcon from "@/components/icons/EyeIcon.vue";
import TrainerDetailModal from "./TrainerDetailModal.vue";
import trainerAcademyService from "@/services/trainerAcademyService";

const loading = ref(false);
const trainersData = ref(null);
const showModal = ref(false);
const selectedTrainer = ref(null);

const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = ref(1);
const sortBy = ref(null);
const sortDir = ref("asc");
const searchQuery = ref("");

const tableHeaders = [
  { text: "Trainer ID", value: "user_id", sortable: true },
  { text: "Name", value: "fullname", sortable: true },
  { text: "Email", value: "email", sortable: true },
  { text: "Registration Date", value: "date_added", sortable: true },
  { text: "Overall Progress", value: "progress", sortable: false },
  { text: "Status", value: "status", sortable: false },
  { text: "Action", value: "action", sortable: false },
];

const trainers = computed(() => {
  if (!trainersData.value || !trainersData.value.rows) {
    return [];
  }

  return trainersData.value.rows.map((trainer) => ({
    id: trainer.user_id,
    user_id: trainer.user_id,
    trainerId: trainer.user_id,
    fullname: trainer.fullname,
    name: trainer.fullname,
    email: trainer.email,
    date_added: trainer.date_added,
    progress: trainer.progress || 0,
    status: trainer.status || "In Training",
  }));
});

const totalTrainers = computed(() => {
  return trainersData.value?.total_rows || trainers.value.length || 0;
});

const certificatesIssued = computed(() => {
  if (!trainers.value || trainers.value.length === 0) return 0;
  return trainers.value.filter((t) => t.status === "Certified").length;
});

const fetchTrainers = async () => {
  try {
    loading.value = true;
    const params = {
      page: currentPage.value,
      limit: itemsPerPage.value,
    };

    if (searchQuery.value) {
      params.search = searchQuery.value;
    }

    if (sortBy.value) {
      params.sort_by = sortBy.value;
      params.sort_dir = sortDir.value;
    }

    const response =
      await trainerAcademyService.adminGetTrainerProgress(params);
    const responseData = response.data?.data;
    trainersData.value = responseData || [];
    totalPages.value = responseData?.total_page || 1;
  } catch (error) {
    console.error("Error fetching trainers:", error);
  } finally {
    loading.value = false;
  }
};

const updatePage = (page) => {
  currentPage.value = page;
  fetchTrainers();
};

const handleSort = (sortData) => {
  sortBy.value = sortData.sort_by;
  sortDir.value = sortData.sort_dir.toLowerCase();
  currentPage.value = 1;
  fetchTrainers();
};

const handleViewDetails = (trainer) => {
  selectedTrainer.value = trainer;
  showModal.value = true;
};

const handleCloseModal = () => {
  showModal.value = false;
  selectedTrainer.value = null;
};

onMounted(() => {
  fetchTrainers();
});
</script>

<style scoped>
.trainer-academy-container {
  background: #f2f3fb;
  padding: 32px;
  border-radius: 16px;
  box-shadow:
    0px 1px 3px 0px rgba(16, 24, 40, 0.1),
    0px 1px 2px 0px rgba(16, 24, 40, 0.06);
}

.trainer-academy-title {
  margin-top: 0px;
  font-size: 36px;
  font-weight: 700;
  color: var(--primary-blue);
  margin-bottom: 32px;
}

.admin-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.metrics-row {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.metric-card {
  flex: 1;
  background: white;
  padding: 12px;
  border-radius: 12px;
  box-shadow:
    0px 6px 10px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
}

.metric-label {
  font-size: var(--fs-medium);
  font-weight: 400;
  color: #98a2b3;
  margin-bottom: 8px;
}

.metric-value {
  font-size: 32px;
  font-weight: 700;
  color: var(--primary-blue);
}

.progress-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-bar-container {
  flex: 1;
  height: 8px;
  background: #bac1cc;
  border-radius: 999px;
  overflow: hidden;
  position: relative;
}

.progress-bar-fill {
  height: 100%;
  background: var(--primary-blue);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: var(--fs-medium);
  font-weight: 400;
  color: #3b4648;
  white-space: nowrap;
  min-width: 45px;
}

.status-badge {
  display: inline-block;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 400;
  color: white;
}

.status-certified {
  background: #56b693;
}

.status-in-training {
  background: #dfe2e7;
  color: #6c737f;
}

.view-details-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: var(--fs-small);
  font-weight: 700;
  color: var(--primary-blue);
  transition: background-color 0.2s ease;
}

.view-details-button:hover {
  background: #f2f3fb;
}

.view-icon {
  width: 16.67px;
  height: 16.67px;
}

@media (max-width: 768px) {
  .metrics-row {
    flex-direction: column;
  }

  .trainer-academy-container {
    padding: 20px;
  }

  .trainer-academy-title {
    font-size: var(--fs-xxl);
  }

  .progress-cell {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .progress-bar-container {
    width: 100%;
  }
}
</style>
